import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PLATFORM_LABELS, formatINR } from "@/lib/helpers";
import { toast } from "sonner";
import { ArrowRight, Lightning, Coins, Warning, ShieldCheck } from "@phosphor-icons/react";
import BuyCreditsModal from "@/components/BuyCreditsModal";

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : (Date.now() + "-" + Math.random().toString(36).slice(2)));

export default function GapFinder() {
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [marketplaces, setMarketplaces] = useState([]);
  const [balance, setBalance] = useState(null);
  const [balanceError, setBalanceError] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [refKey, setRefKey] = useState(uuid()); // idempotency key for current pending run
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [showBuy, setShowBuy] = useState(false);
  const [buyReason, setBuyReason] = useState("");

  const loadBalance = async () => {
    try {
      const r = await api.get("/credits/balance");
      setBalance(r.data.balance);
      setBalanceError(false);
    } catch {
      setBalance(null);
      setBalanceError(true);
    }
  };

  useEffect(() => {
    api.get("/gap-finder").then(r => {
      setData(r.data);
      setMarketplaces((r.data.connected || []).slice(0, 2));
    }).catch(() => toast.error("Failed to load gap finder data"));
    api.get("/products?limit=200").then(r => setProducts(r.data.items || [])).catch(()=>{});
    api.get("/billing/pricing").then(r => setPricing(r.data)).catch(()=>{});
    loadBalance();
  }, []);

  const toggleMp = (p) => {
    setMarketplaces((cur) => cur.includes(p) ? cur.filter(x => x !== p) : [...cur, p]);
  };
  const toggleProduct = (id) => {
    setSelectedIds((cur) => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  };

  const maxMp = pricing?.max_marketplaces_per_run ?? 2;
  const maxPr = pricing?.max_products_per_run ?? 10;
  const creditsRequired = 1;
  const canRun = marketplaces.length > 0 && selectedIds.length > 0
    && marketplaces.length <= maxMp && selectedIds.length <= maxPr;

  const runFinder = async () => {
    if (!canRun || running) return;
    if (balance !== null && balance < creditsRequired) {
      setBuyReason(`You need ${creditsRequired} credit to run this Gap Finder. You have ${balance}.`);
      setShowBuy(true);
      return;
    }
    setRunning(true);
    try {
      const r = await api.post("/gap-finder/run", {
        marketplaces, product_ids: selectedIds, reference_id: refKey,
      });
      setResults(r.data);
      if (r.data.cached) {
        toast("Same request — cached result. No credit consumed.");
      } else {
        toast.success(`Gap Finder complete. 1 credit consumed.`);
      }
      await loadBalance();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402) {
        setBuyReason(`Insufficient credits. Balance: ${(detail && detail.balance) ?? 0}.`);
        setShowBuy(true);
      } else if (status === 400) {
        toast.error(typeof detail === "string" ? detail : "Invalid request");
      } else {
        toast.error("Gap Finder failed. No credit consumed.");
      }
    } finally {
      setRunning(false);
    }
  };

  const resetRun = () => {
    setResults(null);
    setRefKey(uuid()); // fresh idempotency key for the next run
  };

  if (!data) return <div className="font-mono-data text-sm text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="gap-finder-page" className="space-y-6 min-w-0">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-1">Product Gap Finder</div>
          <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter text-navy">
            Where are you losing sales?
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            Compare products across marketplaces. Each successful run consumes 1 credit.
          </p>
        </div>
        <div className="card-base p-4 min-w-[220px]" data-testid="gap-credits-widget">
          <div className="flex items-center gap-2">
            <Coins size={16} className="text-orange" />
            <div className="eyebrow">Credits</div>
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <div
              data-testid="gap-credits-balance"
              className="font-display text-3xl font-bold text-navy tabular-nums"
            >
              {balanceError ? "—" : (balance === null ? "…" : balance)}
            </div>
            <div className="text-xs text-muted-foreground font-mono-data">available</div>
          </div>
          {balanceError && (
            <div className="text-[11px] text-red-600 mt-1">
              Failed to load. <button className="underline" onClick={loadBalance}>Retry</button>
            </div>
          )}
          <button
            data-testid="gap-buy-credits-btn"
            onClick={() => { setBuyReason(""); setShowBuy(true); }}
            className="btn-secondary mt-3 w-full justify-center py-1.5 text-[11px]"
          >
            <Lightning size={12} weight="fill" /> Buy Credits
          </button>
        </div>
      </div>

      {/* Overview KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {data.connected.map(p => (
          <div key={p} className="kpi-card">
            <div className="kpi-label">{PLATFORM_LABELS[p]}</div>
            <div className="kpi-value" style={{ color: "hsl(var(--ecom-orange))" }}>
              {data.platforms[p]?.count || 0}
            </div>
            <div className="kpi-sub">missing listings</div>
          </div>
        ))}
        {data.connected.length === 0 && (
          <div className="card-base p-4 col-span-full text-sm text-muted-foreground">
            Connect a marketplace from Platforms to start finding gaps.
          </div>
        )}
      </div>

      {/* Run controls */}
      {data.connected.length > 0 && (
        <div className="card-base p-6 space-y-4" data-testid="gap-run-panel">
          <div className="section-title flex items-center gap-2">
            <Lightning size={16} weight="fill" className="text-orange" /> Run Gap Finder
          </div>

          <div>
            <div className="eyebrow mb-2">Marketplaces ({marketplaces.length}/{maxMp})</div>
            <div className="flex flex-wrap gap-2">
              {data.connected.map(p => {
                const active = marketplaces.includes(p);
                return (
                  <button
                    key={p}
                    data-testid={`gap-mp-${p}`}
                    onClick={() => toggleMp(p)}
                    className={`pill ${active ? "pill-live" : "pill-neutral"} cursor-pointer`}
                  >
                    {PLATFORM_LABELS[p]} {active && <span className="ml-1">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="eyebrow mb-2 flex items-center justify-between">
              <span>Products ({selectedIds.length}/{maxPr})</span>
              <button
                data-testid="gap-select-first-10"
                onClick={() => setSelectedIds(products.slice(0, maxPr).map(p => p.id))}
                className="text-[11px] font-semibold text-orange uppercase tracking-wider hover:underline"
              >
                Select first {maxPr}
              </button>
            </div>
            <div className="border border-border rounded-lg divide-y divide-border max-h-64 overflow-auto">
              {products.slice(0, 50).map(p => {
                const checked = selectedIds.includes(p.id);
                return (
                  <label
                    key={p.id}
                    data-testid={`gap-product-${p.id}`}
                    className="flex items-center gap-3 p-3 hover:bg-secondary/40 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleProduct(p.id)}
                    />
                    <span className="font-mono-data text-xs text-muted-foreground shrink-0">{p.mpc_id}</span>
                    <span className="text-navy truncate flex-1">{p.name}</span>
                    <span className="text-xs font-mono-data text-muted-foreground shrink-0">
                      {formatINR(p.selling_price)}
                    </span>
                  </label>
                );
              })}
              {products.length === 0 && (
                <div className="p-4 text-sm text-muted-foreground">No products yet.</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Marketplaces" value={marketplaces.length} />
            <Stat label="Products" value={selectedIds.length} />
            <Stat
              label="Credits Available"
              value={balanceError ? "—" : (balance === null ? "…" : balance)}
              tone={balance !== null && balance < creditsRequired ? "red" : "green"}
              testId="gap-stat-available"
            />
            <Stat
              label="Credits Required"
              value={creditsRequired}
              tone="orange"
              testId="gap-stat-required"
            />
          </div>

          {selectedIds.length > maxPr && (
            <div className="text-xs text-red-600 flex items-center gap-1">
              <Warning size={12} /> Plan allows up to {maxPr} products per run.
            </div>
          )}
          {marketplaces.length > maxMp && (
            <div className="text-xs text-red-600 flex items-center gap-1">
              <Warning size={12} /> Plan allows up to {maxMp} marketplaces per run.
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="gap-run-btn"
              disabled={!canRun || running}
              onClick={runFinder}
              className="btn-primary"
            >
              <Lightning size={13} weight="fill" />
              {running ? "Running…" : "Run Gap Finder (1 credit)"}
            </button>
            {results && (
              <button
                data-testid="gap-reset-btn"
                onClick={resetRun}
                className="btn-secondary"
              >
                New Run
              </button>
            )}
          </div>
          <div className="text-[10px] font-mono-data text-muted-foreground uppercase tracking-widest flex items-center gap-1">
            <ShieldCheck size={11} /> Credit deducted only on success · Server-side ledger · Idempotent
          </div>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="card-base p-6" data-testid="gap-results">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="section-title">Results</div>
            <div className="text-xs font-mono-data text-muted-foreground">
              {results.cached ? "CACHED · No credit consumed" : `1 credit consumed · Balance: ${results.balance_after}`}
            </div>
          </div>
          <div className="scroll-x">
            <table className="table-base">
              <thead>
                <tr>
                  <th>MPC</th>
                  <th>Product</th>
                  {marketplaces.map(mp => <th key={mp}>{PLATFORM_LABELS[mp]}</th>)}
                </tr>
              </thead>
              <tbody>
                {(results.results || []).map(row => (
                  <tr key={row.product_id} data-testid={`gap-result-row-${row.product_id}`}>
                    <td className="font-mono-data text-xs">{row.mpc_id}</td>
                    <td className="text-navy">{row.name}</td>
                    {marketplaces.map(mp => {
                      const p = row.platforms[mp] || {};
                      const status = p.status || "MISSING";
                      const pillClass = status === "LIVE" ? "pill-live"
                        : status === "MISSING" ? "pill-missing" : "pill-draft";
                      return (
                        <td key={mp}>
                          <span className={`pill ${pillClass}`}>{status}</span>
                          {p.price !== null && p.price !== undefined && (
                            <div className="text-[11px] font-mono-data text-muted-foreground mt-1">
                              {formatINR(p.price)}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Legacy missing list — quick view */}
      {data.connected.map(p => (
        <div key={p} className="card-base overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between gap-3">
            <div className="section-title truncate">{PLATFORM_LABELS[p]}</div>
            <span className="pill pill-missing">{data.platforms[p]?.count || 0} missing</span>
          </div>
          <div className="divide-y divide-border">
            {(data.platforms[p]?.missing || []).slice(0, 5).map(m => (
              <div key={m.id} className="flex items-center justify-between p-4 gap-3 min-w-0">
                <div className="flex items-center gap-3 min-w-0">
                  {m.image
                    ? <img src={m.image} className="w-10 h-10 object-cover border border-border rounded shrink-0" alt="" onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                    : <div className="w-10 h-10 bg-secondary border border-border rounded shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-navy truncate">{m.name}</div>
                    <div className="text-xs font-mono-data text-muted-foreground truncate">{m.mpc_id} · {formatINR(m.price)}</div>
                  </div>
                </div>
                <ArrowRight size={12} weight="bold" className="text-muted-foreground shrink-0" />
              </div>
            ))}
            {(data.platforms[p]?.count || 0) === 0 && (
              <div className="p-6 text-sm text-muted-foreground text-center">All good on {PLATFORM_LABELS[p]}!</div>
            )}
          </div>
        </div>
      ))}

      <BuyCreditsModal
        open={showBuy}
        onClose={() => setShowBuy(false)}
        onPurchased={async (newBal) => { await loadBalance(); if (newBal !== undefined) setBalance(newBal); setShowBuy(false); }}
        reason={buyReason}
      />
    </div>
  );
}

function Stat({ label, value, tone, testId }) {
  const c = tone === "red" ? "text-red-600" : tone === "green" ? "text-brand-green" : tone === "orange" ? "text-orange" : "text-navy";
  return (
    <div className="border border-border rounded-lg p-3" data-testid={testId}>
      <div className="kpi-label">{label}</div>
      <div className={`font-display font-bold text-2xl tabular-nums ${c}`}>{value}</div>
    </div>
  );
}
